import {match} from 'ts-pattern';

import {uniq} from 'ramda';

import {type PkwEnvkvStatusResponseBody, type PkwEnvkvWorkflowStatus} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {getSafely} from 'shared/web';

/**
 * Backend issue codes that name an editable vehicle field, each mapped to the
 * label key the vehicle edit form uses for that field. The electric (KWH)
 * consumption codes share the base label on purpose — the form labels both
 * variants identically and only the suffix differs.
 */
const VEHICLE_FIELD_ISSUE_LABEL_KEYS: Readonly<Record<string, string>> = {
  MISSING_MAKE: 'entity.vehicle.labels.make',
  MISSING_MODEL: 'entity.vehicle.labels.model',
  MISSING_OR_INVALID_VIN: 'entity.vehicle.labels.vin',
  MISSING_MILEAGE: 'entity.vehicle.labels.mileage',
  MISSING_FIRST_REGISTRATION: 'entity.vehicle.labels.firstRegistration',
  MISSING_CARBON_DIOXIDE_EMISSION: 'entity.vehicle.labels.emissionsCo2WO',
  MISSING_CARBON_DIOXIDE_EMISSION_DISCHARGED:
    'entity.vehicle.labels.carbonDioxideEmissionDischarged',
  MISSING_CARBON_DIOXIDE_EMISSION_WEIGHTED_COMBINED:
    'entity.vehicle.labels.carbonDioxideEmissionWeightedCombined',
  MISSING_FUEL_CONSUMPTION_WLTP_COMBINED: 'entity.vehicle.labels.fuelConsumptionWltpCombined',
  MISSING_FUEL_CONSUMPTION_WLTP_CITY_CENTER: 'entity.vehicle.labels.fuelConsumptionWltpCityCenter',
  MISSING_FUEL_CONSUMPTION_WLTP_OUTSKIRTS: 'entity.vehicle.labels.fuelConsumptionWltpOutskirts',
  MISSING_FUEL_CONSUMPTION_WLTP_COUNTRY_ROADS:
    'entity.vehicle.labels.fuelConsumptionWltpCountryRoads',
  MISSING_FUEL_CONSUMPTION_WLTP_HIGHWAY: 'entity.vehicle.labels.fuelConsumptionWltpHighway',
  MISSING_FUEL_CONSUMPTION_WLTP_WEIGHTED: 'entity.vehicle.labels.fuelConsumptionWltpWeighted',
  MISSING_FUEL_CONSUMPTION_WLTP_COMBINED_KWH: 'entity.vehicle.labels.fuelConsumptionWltpCombined',
  MISSING_FUEL_CONSUMPTION_WLTP_CITY_CENTER_KWH:
    'entity.vehicle.labels.fuelConsumptionWltpCityCenter',
  MISSING_FUEL_CONSUMPTION_WLTP_OUTSKIRTS_KWH: 'entity.vehicle.labels.fuelConsumptionWltpOutskirts',
  MISSING_FUEL_CONSUMPTION_WLTP_COUNTRY_ROADS_KWH:
    'entity.vehicle.labels.fuelConsumptionWltpCountryRoads',
  MISSING_FUEL_CONSUMPTION_WLTP_HIGHWAY_KWH: 'entity.vehicle.labels.fuelConsumptionWltpHighway',
  MISSING_FUEL_CONSUMPTION_WLTP_WEIGHTED_COMBINED_KWH:
    'entity.vehicle.labels.fuelConsumptionWltpWeightedCombinedKwh',
  MISSING_ELECTRIC_RANGE: 'entity.vehicle.labels.batteryElectricRange',
  UNSUPPORTED_DRIVE: 'entity.vehicle.labels.driveType',
  MISSING_SECONDARY_FUEL_TYPE: 'entity.vehicle.labels.secondaryFuelType',
  CLASSIFICATION_UNKNOWN: 'entity.vehicle.labels.vehicleType',
};

/**
 * Backend issue codes that do not name an editable vehicle field. The CO2
 * classes come from the EnVKV calculation; hydrogen inputs are not supported.
 */
const OTHER_ISSUE_LABEL_KEYS: Readonly<Record<string, string>> = {
  MISSING_OR_OUTDATED_PRIMARY_CO2_CLASS: 'page.pkwEnvkv.advertisingIssues.primaryCo2Class',
  MISSING_OR_OUTDATED_SECONDARY_CO2_CLASS: 'page.pkwEnvkv.advertisingIssues.secondaryCo2Class',
  UNSUPPORTED_HYDROGEN_INPUTS: 'page.pkwEnvkv.advertisingIssues.unsupportedHydrogenInputs',
};

const UNKNOWN_ISSUE_LABEL_KEY = 'page.pkwEnvkv.advertisingIssues.unknown';

export function isPkwEnvkvVehicleFieldIssue(code: string): boolean {
  return code in VEHICLE_FIELD_ISSUE_LABEL_KEYS;
}

// Inputs that decide whether EnVKV applies at all.
const APPLICABILITY_ISSUE_CODES: ReadonlySet<string> = new Set([
  'MISSING_MILEAGE',
  'MISSING_FIRST_REGISTRATION',
  'CLASSIFICATION_UNKNOWN',
]);

export function isPkwEnvkvApplicabilityIssue(code: string): boolean {
  return APPLICABILITY_ISSUE_CODES.has(code);
}

export type PkwEnvkvVehicleEditTab = 'basic-information' | 'vehicle-data';

/**
 * The vehicle edit form keeps almost every EnVKV-relevant field (VIN, make,
 * model, fuel and drive, vehicle type, first registration, all WLTP and CO2
 * consumption fields) on the basic-information tab; only the mileage lives on
 * the vehicle-data tab.
 */
const VEHICLE_DATA_TAB_ISSUE_CODES: ReadonlySet<string> = new Set(['MISSING_MILEAGE']);

function vehicleEditTabFor(code: string): PkwEnvkvVehicleEditTab {
  return VEHICLE_DATA_TAB_ISSUE_CODES.has(code) ? 'vehicle-data' : 'basic-information';
}

/**
 * The vehicle edit tabs that hold the editable fields behind the given issue
 * codes, in the order the tabs appear in the form. Issues from different tabs
 * yield one entry per tab, so every issue links to the tab that holds it.
 */
export function getPkwEnvkvVehicleEditTabs(codes: string[]): PkwEnvkvVehicleEditTab[] {
  const fieldCodes = codes.filter(isPkwEnvkvVehicleFieldIssue);
  const tabs: PkwEnvkvVehicleEditTab[] = [];
  if (fieldCodes.some((code) => vehicleEditTabFor(code) === 'basic-information')) {
    tabs.push('basic-information');
  }
  if (fieldCodes.some((code) => vehicleEditTabFor(code) === 'vehicle-data')) {
    tabs.push('vehicle-data');
  }

  return tabs;
}

/**
 * The vehicle edit tab that the single edit link opens: the tab of a missing
 * applicability input first, because it decides whether EnVKV applies, then
 * the first tab in form order.
 */
export function getPkwEnvkvVehicleEditTab(codes: string[]): PkwEnvkvVehicleEditTab | undefined {
  return [
    ...getPkwEnvkvVehicleEditTabs(codes.filter(isPkwEnvkvApplicabilityIssue)),
    ...getPkwEnvkvVehicleEditTabs(codes),
  ].at(0);
}

export function isPkwEnvkvCalculatedValueIssue(code: string): boolean {
  return (
    code === 'MISSING_OR_OUTDATED_PRIMARY_CO2_CLASS' ||
    code === 'MISSING_OR_OUTDATED_SECONDARY_CO2_CLASS'
  );
}

export type PkwEnvkvIssueGroup =
  | {kind: 'vehicle-fields'; tab: PkwEnvkvVehicleEditTab; labels: string[]}
  | {kind: 'calculated-values'; labels: string[]}
  | {kind: 'other'; labels: string[]};

/**
 * Groups issue codes by the action that corrects them: editable vehicle
 * fields per vehicle edit tab, values the EnVKV calculation produces, and
 * everything else. Groups keep the tab order of the form and empty groups are
 * omitted, so each rendered group can carry exactly one correction action.
 */
export function groupPkwEnvkvIssues(codes: string[]): PkwEnvkvIssueGroup[] {
  const groups: PkwEnvkvIssueGroup[] = [];
  const fieldCodes = codes.filter(isPkwEnvkvVehicleFieldIssue);
  for (const tab of getPkwEnvkvVehicleEditTabs(codes)) {
    groups.push({
      kind: 'vehicle-fields',
      tab,
      labels: getPkwEnvkvIssueLabels(fieldCodes.filter((code) => vehicleEditTabFor(code) === tab)),
    });
  }

  const calculatedCodes = codes.filter(isPkwEnvkvCalculatedValueIssue);
  if (calculatedCodes.length > 0) {
    groups.push({kind: 'calculated-values', labels: getPkwEnvkvIssueLabels(calculatedCodes)});
  }

  const otherCodes = codes.filter(
    (code) => !isPkwEnvkvVehicleFieldIssue(code) && !isPkwEnvkvCalculatedValueIssue(code)
  );
  if (otherCodes.length > 0) {
    groups.push({kind: 'other', labels: getPkwEnvkvIssueLabels(otherCodes)});
  }

  return groups;
}

/**
 * The workflow statuses from which the backend accepts a new EnVKV
 * calculation. Kept in sync with `CalculatePkwEnvkvCommandHandler`; a current
 * or generated document must not be recalculated because that would drop the
 * current document link.
 */
const CALCULABLE_WORKFLOW_STATUSES: ReadonlySet<PkwEnvkvWorkflowStatus> =
  new Set<PkwEnvkvWorkflowStatus>(['READY_TO_CALCULATE', 'OUTDATED', 'CALCULATION_FAILED']);

export function canCalculatePkwEnvkv(workflowStatus: PkwEnvkvWorkflowStatus): boolean {
  return CALCULABLE_WORKFLOW_STATUSES.has(workflowStatus);
}

/**
 * The issues to correct before a blocked vehicle can be advertised. A missing
 * applicability input always comes along: until it is filled in, the backend
 * cannot tell whether EnVKV applies. A missing calculated advertising value
 * can be corrected only after the existing calculation input is complete, so
 * all issues come along when the current workflow cannot start the
 * calculation yet.
 */
export function getPkwEnvkvAdvertisingCorrectionIssueCodes(
  status: PkwEnvkvStatusResponseBody
): string[] {
  return match(status)
    .when(
      (current) => current.advertisingStatus !== 'RED',
      (current) => current.advertisingIssues
    )
    .when(
      (current) =>
        !canCalculatePkwEnvkv(current.workflowStatus) &&
        current.advertisingIssues.some(isPkwEnvkvCalculatedValueIssue),
      (current) => uniq([...current.advertisingIssues, ...current.issues])
    )
    .otherwise((current) =>
      uniq([...current.advertisingIssues, ...current.issues.filter(isPkwEnvkvApplicabilityIssue)])
    );
}

/**
 * Translates backend issue codes to user-facing labels. A raw backend code
 * never reaches the user: a code this frontend does not know collapses into
 * one generic line, and codes sharing a form label collapse into one line.
 */
export function getPkwEnvkvIssueLabels(codes: string[]): string[] {
  return uniq(
    codes.map((code) =>
      i18n.t(
        getSafely(VEHICLE_FIELD_ISSUE_LABEL_KEYS, code) ??
          getSafely(OTHER_ISSUE_LABEL_KEYS, code) ??
          UNKNOWN_ISSUE_LABEL_KEY
      )
    )
  );
}
